import { createContext, useContext, useState, useCallback, useRef, type ReactNode } from 'react';
import { X } from 'lucide-react';

export interface ConfirmOptions {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'warning' | 'info';
}

interface ConfirmContextValue {
  confirm: (options: ConfirmOptions | string) => Promise<boolean>;
}

const ConfirmContext = createContext<ConfirmContextValue>({ confirm: async () => true });

type ModalState = ConfirmOptions & { open: boolean };

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ModalState | null>(null);
  const resolveRef = useRef<((v: boolean) => void) | null>(null);

  const confirm = useCallback((options: ConfirmOptions | string): Promise<boolean> => {
    // Si confirmActions está desactivado, aprobar automáticamente
    try {
      const prefs = JSON.parse(localStorage.getItem('cn_user_preferences') || '{}');
      if (prefs.confirmActions === false) return Promise.resolve(true);
    } catch {}

    const opts = typeof options === 'string' ? { message: options } : options;

    return new Promise<boolean>(resolve => {
      resolveRef.current = resolve;
      setState({ ...opts, open: true });
    });
  }, []);

  const handleClose = (result: boolean) => {
    setState(null);
    resolveRef.current?.(result);
    resolveRef.current = null;
  };

  const variantColors: Record<string, string> = {
    danger: 'bg-red-500 hover:bg-red-600',
    warning: 'bg-orange-500 hover:bg-orange-600',
    info: 'bg-[#456fb5] hover:bg-[#3a5fa0]',
  };
  const btnColor = variantColors[state?.variant ?? 'info'];

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}

      {state?.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[200] p-4">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl w-full max-w-sm animate-zoom-in-95">
            <div className="flex items-start justify-between p-6 pb-3">
              <h3 className="text-base font-bold text-slate-800 dark:text-slate-100 pr-4">
                {state.title ?? 'Confirmar acción'}
              </h3>
              <button
                onClick={() => handleClose(false)}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 shrink-0"
              >
                <X size={18} />
              </button>
            </div>
            <p className="px-6 pb-6 text-sm text-slate-500 dark:text-slate-400">
              {state.message}
            </p>
            <div className="flex gap-3 px-6 pb-6">
              <button
                onClick={() => handleClose(false)}
                className="flex-1 py-2.5 border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 rounded-lg text-sm font-semibold hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
              >
                {state.cancelLabel ?? 'Cancelar'}
              </button>
              <button
                onClick={() => handleClose(true)}
                className={`flex-1 py-2.5 rounded-lg text-sm font-bold text-white transition-colors ${btnColor}`}
              >
                {state.confirmLabel ?? 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

/** Devuelve una función `confirm(options)` que retorna Promise<boolean> */
export const useConfirm = () => useContext(ConfirmContext).confirm;
